import { useState, useEffect, useRef } from 'react';

type Position = { x: number; y: number };

type UseDraggableChatOptions = {
	isOpen: boolean;
	onToggle?: () => void;
	onClose?: () => void;
	defaultPosition?: Position;
	minHeight?: number;
	minWidth?: number;
	padding?: number;
	leftPadding?: number;
	snapThreshold?: number;
	resizeHandleSize?: number;
};

const viewportWidth = () => document.documentElement.clientWidth;

export const useDraggableChat = (options: UseDraggableChatOptions = { isOpen: false }) => {
	const {
		isOpen,
		onToggle,
		onClose,
		defaultPosition = { x: 60, y: 90 },
		minHeight = 500,
		minWidth = 400,
		padding = 10,
		leftPadding = 10,
		snapThreshold = 300,
		resizeHandleSize = 20
	} = options;

	const [position, setPosition] = useState<Position>(() => {
		const saved = localStorage.getItem('assistantPosition');
		if (saved) {
			try {
				return JSON.parse(saved);
			} catch {
				return defaultPosition;
			}
		}
		return defaultPosition;
	});

	const [isDragging, setIsDragging] = useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [resizeDirection, setResizeDirection] = useState<string | null>(null);
	const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
	const [resizeStart, setResizeStart] = useState<{
		mouseX: number;
		mouseY: number;
		containerX: number;
		containerY: number;
		width: number;
		height: number;
	} | null>(null);
	const [snapHotspot, setSnapHotspot] = useState<'left' | 'right' | null>(null);
	const [containerWidth, setContainerWidth] = useState<number>(500);
	const containerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState<number | null>(() => {
		const saved = localStorage.getItem('assistantWidth');
		return saved ? parseInt(saved, 10) : null;
	});
	const [height, setHeight] = useState<number | null>(() => {
		const saved = localStorage.getItem('assistantHeight');
		return saved ? parseInt(saved, 10) : null;
	});
	const [previousDimensions, setPreviousDimensions] = useState<{
		width: number | null;
		height: number;
		x: number;
		y: number;
	} | null>(null);

	useEffect(() => {
		localStorage.setItem('assistantPosition', JSON.stringify(position));
	}, [position]);

	useEffect(() => {
		if (width !== null) {
			localStorage.setItem('assistantWidth', width.toString());
		}
	}, [width]);

	useEffect(() => {
		if (height !== null) {
			localStorage.setItem('assistantHeight', height.toString());
		}
	}, [height]);

	// Reset position if chat doesn't fit window when opening
	const prevIsOpenRef = useRef(isOpen);
	useEffect(() => {
		if (!isOpen || prevIsOpenRef.current === isOpen) {
			prevIsOpenRef.current = isOpen;
			return;
		}
		prevIsOpenRef.current = isOpen;

		const currentWidth = width ?? 500;
		const currentHeight = height ?? minHeight;
		const maxX = viewportWidth() - currentWidth - padding;
		const maxY = window.innerHeight - currentHeight - padding;

		const isOutOfBounds =
			position.x < 0 ||
			position.x > maxX ||
			position.y < padding ||
			position.y > maxY ||
			currentWidth > viewportWidth() - padding * 2 ||
			currentHeight > window.innerHeight - padding * 2;

		if (isOutOfBounds) {
			const availableHeight = window.innerHeight - defaultPosition.y - padding;
			setPosition(defaultPosition);
			setWidth(null);
			setHeight(Math.max(minHeight, availableHeight));
		}
	}, [isOpen, defaultPosition, minHeight, padding, width, height, position]);

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.shiftKey) return;

			if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
				e.preventDefault();
				onToggle?.();
			} else if (e.key === 'Escape' && isOpen) {
				onClose?.();
			} else if ((e.ctrlKey || e.metaKey) && isOpen && containerRef.current) {
				const cw = containerRef.current.offsetWidth || 500;
				const ch = containerRef.current.offsetHeight || 400;
				let newPosition = { ...position };

				switch (e.key) {
					case 'ArrowRight':
						e.preventDefault();
						newPosition = { x: viewportWidth() - cw - padding, y: position.y };
						break;
					case 'ArrowLeft':
						e.preventDefault();
						newPosition = { x: leftPadding, y: position.y };
						break;
					case 'ArrowUp':
						e.preventDefault();
						newPosition = { x: position.x, y: padding };
						break;
					case 'ArrowDown':
						e.preventDefault();
						newPosition = { x: position.x, y: window.innerHeight - ch - padding };
						break;
					default:
						return;
				}

				const maxX = viewportWidth() - cw;
				const maxY = window.innerHeight - ch;
				setPosition({
					x: Math.max(0, Math.min(maxX, newPosition.x)),
					y: Math.max(0, Math.min(maxY, newPosition.y))
				});
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, position, onToggle, onClose, padding, leftPadding]);

	const getResizeDirection = (e: React.MouseEvent<HTMLDivElement>): string | null => {
		if (!containerRef.current) return null;
		const rect = containerRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		const isLeft = x < resizeHandleSize;
		const isRight = x > rect.width - resizeHandleSize;
		const isBottom = y > rect.height - resizeHandleSize;

		if (isLeft && isBottom) return 'sw';
		if (isRight && isBottom) return 'se';
		if (isLeft) return 'w';
		if (isRight) return 'e';
		if (isBottom) return 's';
		return null;
	};

	const isMaximized = () => {
		const maxHeight = window.innerHeight - padding * 2;
		const maxWidth = viewportWidth() - leftPadding - padding;
		return (
			height !== null &&
			width !== null &&
			Math.abs(height - maxHeight) < 10 &&
			Math.abs(width - maxWidth) < 10 &&
			Math.abs(position.y - padding) < 10 &&
			Math.abs(position.x - leftPadding) < 10
		);
	};

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!containerRef.current) return;

		const resizeDir = getResizeDirection(e);
		if (resizeDir) {
			const rect = containerRef.current.getBoundingClientRect();
			setResizeStart({
				mouseX: e.clientX,
				mouseY: e.clientY,
				containerX: position.x,
				containerY: position.y,
				width: rect.width,
				height: rect.height
			});
			setResizeDirection(resizeDir);
			setIsResizing(true);
			e.preventDefault();
			return;
		}

		if (isMaximized()) return;

		const rect = containerRef.current.getBoundingClientRect();
		setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
		setContainerWidth(rect.width);
		setIsDragging(true);
		e.preventDefault();
	};

	// Drag move
	useEffect(() => {
		if (!isDragging) return;
		let animationFrameId: number;

		const handleMouseMove = (e: MouseEvent) => {
			if (animationFrameId) cancelAnimationFrame(animationFrameId);
			animationFrameId = requestAnimationFrame(() => {
				const newX = e.clientX - dragOffset.x;
				const newY = e.clientY - dragOffset.y;
				const containerHeight = containerRef.current?.offsetHeight || 400;
				const maxX = viewportWidth() - containerWidth;
				const maxY = window.innerHeight - containerHeight - padding;
				const constrainedX = Math.max(0, Math.min(maxX, newX));

				if (constrainedX < snapThreshold) {
					setSnapHotspot('left');
				} else if (constrainedX > maxX - snapThreshold) {
					setSnapHotspot('right');
				} else {
					setSnapHotspot(null);
				}

				setPosition({ x: constrainedX, y: Math.max(padding, Math.min(maxY, newY)) });
			});
		};

		const handleMouseUp = (e: MouseEvent) => {
			if (!containerRef.current) {
				setIsDragging(false);
				setSnapHotspot(null);
				return;
			}

			const finalX = e.clientX - dragOffset.x;
			const finalY = e.clientY - dragOffset.y;
			const cw = containerRef.current.offsetWidth || 500;
			const ch = containerRef.current.offsetHeight || 400;
			const maxX = viewportWidth() - cw;
			const maxY = window.innerHeight - ch - padding;
			const constrainedX = Math.max(0, Math.min(maxX, finalX));
			const constrainedY = Math.max(padding, Math.min(maxY, finalY));

			let snappedX = constrainedX;
			let shouldMaximizeHeight = false;

			if (constrainedX < snapThreshold) {
				snappedX = leftPadding;
				shouldMaximizeHeight = true;
			} else if (constrainedX > maxX - snapThreshold) {
				snappedX = maxX - padding;
				shouldMaximizeHeight = true;
			}

			if (shouldMaximizeHeight) {
				setPosition({ x: Math.max(0, Math.min(maxX, snappedX)), y: padding });
				setHeight(window.innerHeight - padding * 2);
			} else {
				setPosition({ x: Math.max(0, Math.min(maxX, snappedX)), y: constrainedY });
			}

			setIsDragging(false);
			setSnapHotspot(null);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			if (animationFrameId) cancelAnimationFrame(animationFrameId);
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging, dragOffset, containerWidth, padding, snapThreshold, leftPadding]);

	// Resize logic
	useEffect(() => {
		if (!isResizing || !resizeStart || !resizeDirection) return;

		const handleMouseMove = (e: MouseEvent) => {
			if (!containerRef.current) return;
			const clientX = Math.max(0, Math.min(viewportWidth(), e.clientX));
			const clientY = Math.max(0, Math.min(window.innerHeight, e.clientY));
			const deltaX = clientX - resizeStart.mouseX;
			const deltaY = clientY - resizeStart.mouseY;

			let newWidth = resizeStart.width;
			let newHeight = resizeStart.height;
			let newX = resizeStart.containerX;
			const newY = resizeStart.containerY;

			if (resizeDirection.includes('e')) {
				const maxW = viewportWidth() - resizeStart.containerX - padding;
				newWidth = Math.max(minWidth, Math.min(maxW, resizeStart.width + deltaX));
			}
			if (resizeDirection.includes('w')) {
				const potentialWidth = resizeStart.width - deltaX;
				const maxW = viewportWidth() - leftPadding - padding;
				const constrainedWidth = Math.max(minWidth, Math.min(maxW, potentialWidth));
				newX = resizeStart.containerX + (resizeStart.width - constrainedWidth);
				newWidth = constrainedWidth;
			}
			if (resizeDirection.includes('s')) {
				newHeight = Math.max(minHeight, resizeStart.height + deltaY);
			}

			const maxX = viewportWidth() - newWidth - padding;
			const minX = resizeDirection.includes('w') ? leftPadding : padding;
			if (newX < minX) {
				if (resizeDirection.includes('w')) {
					newWidth = Math.max(minWidth, newWidth - (minX - newX));
				}
				newX = minX;
			}
			if (newX > maxX) {
				if (resizeDirection.includes('w')) {
					newWidth = Math.max(minWidth, newWidth - (newX - maxX));
				}
				newX = maxX;
			}

			newWidth = Math.min(newWidth, viewportWidth() - newX - padding);
			newHeight = Math.min(newHeight, window.innerHeight - newY - padding);

			setPosition({ x: newX, y: newY });
			setWidth(newWidth);
			if (resizeDirection.includes('s')) setHeight(newHeight);
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			setResizeDirection(null);
			setResizeStart(null);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isResizing, resizeStart, resizeDirection, minWidth, minHeight, padding, leftPadding]);

	const handleDoubleClick = () => {
		const maxHeight = window.innerHeight - padding * 2;
		const maxWidth = viewportWidth() - leftPadding - padding;

		if (isMaximized()) {
			if (previousDimensions) {
				setWidth(previousDimensions.width);
				setHeight(previousDimensions.height);
				setPosition({ x: previousDimensions.x, y: previousDimensions.y });
				setPreviousDimensions(null);
			} else {
				setHeight(minHeight);
				setWidth(null);
			}
		} else {
			setPreviousDimensions({ width, height: height ?? minHeight, x: position.x, y: position.y });
			setPosition({ x: leftPadding, y: padding });
			setWidth(maxWidth);
			setHeight(maxHeight);
		}
	};

	const getResizeCursor = (resizeDir: string | null): string => {
		if (!resizeDir) return 'default';
		const cursors: Record<string, string> = {
			nw: 'nwse-resize',
			ne: 'nesw-resize',
			sw: 'nesw-resize',
			se: 'nwse-resize',
			n: 'ns-resize',
			s: 'ns-resize',
			e: 'ew-resize',
			w: 'ew-resize'
		};
		return cursors[resizeDir] || 'default';
	};

	return {
		position,
		width,
		height,
		isDragging,
		isResizing,
		resizeDirection,
		snapHotspot,
		containerWidth,
		containerRef,
		handleMouseDown,
		handleDoubleClick,
		isMaximized: isMaximized(),
		getResizeDirection,
		getResizeCursor
	};
};
